import {
  type StoredCard,
  CardScheme,
  type AppContext,
} from "@/types";
import { resolveScheme } from "@/lib/card";
import { encodeCardScheme } from "@/lib/codec";
import type { PpsPaymentMethodDetails } from "@/modules/checkout-session";
import {
  PAYMENT_METHOD_TYPE,
  type PaymentMethodOption,
} from "../types";

const SCHEME_DISPLAY_NAME = new Map<CardScheme, string>([
  [CardScheme.MASTERCARD, "Mastercard"],
  [CardScheme.VISA, "Visa"],
  [CardScheme.AMEX, "AMEX"],
  [CardScheme.MADA, "mada"],
  [CardScheme.KNET, "KNET"],
]);

export function getPaymentMethodName(option: PaymentMethodOption): string {
  if (option.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
    return "Apple Pay";
  }
  return (
    SCHEME_DISPLAY_NAME.get(
      option.card ? resolveScheme(option.card) : CardScheme.UNKNOWN,
    ) ?? "Unknown"
  );
}

export function canPayWithApple(
  ctx: AppContext,
  applePayPreference: boolean = true,
): boolean {
  return (
    ctx.session.country.applePayAllowed &&
    ctx.applePay.deviceSupports() &&
    applePayPreference
  );
}

export function buildPaymentMethodsList(
  savedCards: StoredCard[],
  includeApplePay: boolean,
): PaymentMethodOption[] {
  const methods: PaymentMethodOption[] = savedCards.map((card) => ({
    paymentMethodType: PAYMENT_METHOD_TYPE.STORED_CARD,
    card,
  }));

  if (includeApplePay) {
    return [
      { paymentMethodType: PAYMENT_METHOD_TYPE.APPLE, card: undefined },
      ...methods,
    ];
  }

  return methods;
}

export function selectPaymentMethod(
  method: PaymentMethodOption,
): PpsPaymentMethodDetails {
  return {
    type:
      method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE
        ? "apple_pay"
        : "card",
    id: (method.card?.id ?? "") + "",
    name: "",
    last4: method.card?.last4 ?? "",
    icon: "",
  };
}

export function isPaymentMethodSelected(
  method: PaymentMethodOption,
  selectedMethod?: PpsPaymentMethodDetails,
): boolean {
  if (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
    return selectedMethod?.type === "apple_pay";
  }
  return selectedMethod?.id === method.card?.id + "";
}

export function getCardTrackingData(
  method: PaymentMethodOption,
): Record<string, unknown> {
  if (!method.card) return {};

  return {
    card_id: method.card.id,
    card_scheme: encodeCardScheme(method.card.scheme ?? CardScheme.UNKNOWN),
    card_scheme_local: encodeCardScheme(
      method.card.schemeLocal ?? CardScheme.UNKNOWN,
    ),
    card_is_debit: method.card.isDebit,
    card_is_prepaid: method.card.isPrepaid,
    card_is_auto_charge: method.card.isAutoCharge,
    card_is_bin_blacklisted: method.card.isBinBlacklisted,
    card_is_disabled_cvv: method.card.isDisabledCvv,
  };
}

export function shouldPayWithWalletOnly(
  isWalletSelected: boolean,
  canWalletCoverDownPayment: boolean | undefined,
): boolean {
  return isWalletSelected && Boolean(canWalletCoverDownPayment);
}

export function calculateAmountToPay(
  ctx: AppContext,
  isWalletSelected: boolean,
): number {
  let amountToPayNow = ctx.selectedPayment().amountToPayNow;

  if (isWalletSelected) {
    const cardAmountWithWallet =
      ctx.session.paymentDetails?.cardDownPaymentAmountWithWallet;

    if (cardAmountWithWallet !== undefined) {
      // Zero is valid when the wallet fully covers the down payment.
      amountToPayNow = cardAmountWithWallet.amount;
    } else {
      console.warn(
        `Wallet was selected but could not resolve card down payment amount with wallet. Defaulting to amount without wallet: ${amountToPayNow}`,
      );
    }
  }

  return amountToPayNow;
}

/**
 * @deprecated Temporary re-export for backward compatibility.
 * Consumers should import individual functions directly.
 */
export const PaymentMethodUtils = {
  getPaymentMethodName,
  canPayWithApple,
  buildPaymentMethodsList,
  selectPaymentMethod,
  isPaymentMethodSelected,
  getCardTrackingData,
  shouldPayWithWalletOnly,
  calculateAmountToPay,
};
